import {type IDMappedObjects, type RelationOneToMany, type RelationOneToOne} from './utilities';
import {type Team} from './teams';
import {type ChannelModerationRoles} from './roles';

// e.g.
// **O**pen channel,
// **P**rivate channel,
// **D**irect message to one other,
// **G**roup direct message to 2+ others
export enum ChannelType {
    Open = 'O',
    Private = 'P',
    Direct = 'D',
    Group = 'G',
}

export type ChannelStats = {
    channel_id: string;
    member_count: number;
    guest_count: number;
    restricted_guest_count: number;
    pinnedpost_count: number;
    files_count: number;
};

export type ChannelNotifyProps = {
    desktop: 'default' | 'all' | 'mention' | 'none';
    email: 'default' | 'all' | 'mention' | 'none';
    mark_unread: 'all' | 'mention';
    push: 'default' | 'all' | 'mention' | 'none';
    ignore_channel_mentions: 'default' | 'off' | 'on';
};

interface ChannelProps {
    create_at: number;
    creator_id: string;
    delete_at: number;
    extra_update_at: number;
    group_constrained: boolean | null;
    header: string;
    id: string;
    last_post_at: number;
    last_root_post_at: number;
    leave_join_posts: number;
    name: string;
    policy_id?: string | null;
    props?: Record<string, any> | null;
    purpose: string;
    scheme_id: string | null;
    shared?: boolean | null;
    team_id: string;
    type: ChannelType;
    update_at: number;
}

export type DirectChannelName = `${string}__${string}`;
export type TeammateStatus = 'online' | 'away' | 'dnd' | 'offline';

export interface DirectChannel extends ChannelProps {
    type: ChannelType.Direct;
    name: DirectChannelName;
}

export interface GroupChannel extends ChannelProps {
    type: ChannelType.Group;
    display_name: string;
}

export interface OpenChannel extends ChannelProps {
    type: ChannelType.Open;
    display_name: string;
}

export interface PrivateChannel extends ChannelProps {
    type: ChannelType.Private;
    display_name: string;
}

export type Channel = DirectChannel | GroupChannel | OpenChannel | PrivateChannel;

export type ServerChannel = Channel & {

    /**
     * The total number of posts in this channel, not including join/leave messages
     *
     * @remarks This field will be moved to a {@link ChannelMessageCount} object when this channel is stored in Redux.
     */
    total_msg_count: number;

    /**
     * The number of root posts in this channel, not including join/leave messages
     *
     * @remarks This field will be moved to a {@link ChannelMessageCount} object when this channel is stored in Redux.
     */
    total_msg_count_root: number;
}

export type ChannelMessageCount = {

    /** The total number of posts in this channel, not including join/leave messages */
    total: number;

    /** The number of root posts in this channel, not including join/leave messages */
    root: number;
}

export type ChannelWithTeamData = Channel & {
    team_display_name: string;
    team_name: string;
    team_update_at: number;
};

export type ChannelsWithTotalCount = {
    channels: ChannelWithTeamData[];
    total_count: number;
};

export type ChannelMembership = {
    channel_id: string;
    user_id: string;
    roles: string;
    last_viewed_at: number;

    /** The number of posts in this channel which have been read by the user */
    msg_count: number;

    /** The number of root posts in this channel which have been read by the user */
    msg_count_root: number;

    /** The number of unread mentions in this channel */
    mention_count: number;

    /** The number of unread mentions in root posts in this channel */
    mention_count_root: number;

    notify_props: Partial<ChannelNotifyProps>;
    last_update_at: number;
    scheme_user: boolean;
    scheme_admin: boolean;
    scheme_guest: boolean;
    scheme_restricted_guest: boolean;
    post_root_id?: string;
};

export type ChannelUnread = {
    channel_id: string;
    user_id: string;
    team_id: string;

    /** The number of posts which have been read by the user */
    msg_count: number;

    /** The number of root posts which have been read by the user */
    msg_count_root: number;

    /** The number of unread mentions in this channel */
    mention_count: number;

    /** The number of unread mentions in root posts in this channel */
    mention_count_root: number;

    last_viewed_at: number;
};

export type ChannelsState = {
    currentChannelId: string;
    channels: IDMappedObjects<Channel>;
    channelsInTeam: RelationOneToMany<Team, Channel>;
    myMembers: RelationOneToOne<Channel, ChannelMembership>;
    roles: RelationOneToOne<Channel, Set<string>>;
    membersInChannel: RelationOneToOne<Channel, Record<string, ChannelMembership>>;
    stats: RelationOneToOne<Channel, ChannelStats>;
    groupsAssociatedToChannel: any;
    totalCount: number;
    manuallyUnread: RelationOneToOne<Channel, boolean>;
    channelModerations: RelationOneToOne<Channel, ChannelModeration[]>;
    channelMemberCountsByGroup: RelationOneToOne<Channel, ChannelMemberCountsByGroup>;
    messageCounts: RelationOneToOne<Channel, ChannelMessageCount>;
};

export type ChannelModeration = {
    name: string;
    roles: Record<ChannelModerationRoles, {
        value: boolean;
        enabled: boolean;
    } | undefined>;
};

export type ChannelModerationPatch = {
    name: string;
    roles: Record<ChannelModerationRoles, boolean | undefined>;
};

export type ChannelMemberCountByGroup = {
    group_id: string;
    channel_member_count: number;
    channel_member_timezones_count: number;
};

export type ChannelMemberCountsByGroup = Record<string, ChannelMemberCountByGroup>;

export type ChannelGroupsMembersCount = {
    group_id: string;
    channel_member_count: number;
    channel_member_timezones_count: number;
}

export type ChannelViewResponse = {
    status: string;
    last_viewed_at_times: RelationOneToOne<Channel, number>;
};

export type ChannelSearchOpts = {
    nonAdminSearch?: boolean;
    exclude_default_channels?: boolean;
    not_associated_to_group?: string;
    team_ids?: string[];
    group_constrained?: boolean;
    exclude_group_constrained?: boolean;
    public?: boolean;
    private?: boolean;
    include_deleted?: boolean;
    deleted?: boolean;
    page?: number;
    per_page?: number;
};
